// Custom hook for news data management
import { useState, useEffect, useCallback } from 'react';
import { NewsArticle } from '../services/newsService';
import { searchStaticNews, getPaginatedNews } from '../data/staticNews';

export interface UseNewsState {
  news: NewsArticle[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  currentPage: number;
  totalPages: number;
  total: number;
}

export interface UseNewsActions {
  fetchNews: (page?: number, limit?: number) => Promise<void>;
  searchNews: (query: string, limit?: number) => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export interface UseNewsReturn extends UseNewsState, UseNewsActions {}

interface UseNewsOptions {
  initialLimit?: number;
  autoFetch?: boolean;
}

export const useNews = (options: UseNewsOptions = {}): UseNewsReturn => {
  const {
    initialLimit = 6,
    autoFetch = true
  } = options;

  const [state, setState] = useState<UseNewsState>({
    news: [],
    loading: false,
    error: null,
    hasMore: true,
    currentPage: 1,
    totalPages: 1,
    total: 0
  });

  const updateState = useCallback((updates: Partial<UseNewsState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const handleError = useCallback((error: unknown) => {
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
    console.error('News hook error:', error);
    updateState({ error: errorMessage, loading: false });
  }, [updateState]);

  const fetchNews = useCallback(async (page = 1, limit = initialLimit) => {
    try {
      updateState({ loading: true, error: null });
      
      // Simulate API delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const result = getPaginatedNews(page, limit);
      
      updateState({
        news: page === 1 ? result.news : [...state.news, ...result.news],
        loading: false,
        currentPage: page,
        totalPages: result.totalPages,
        total: result.total,
        hasMore: result.hasMore
      });
    } catch (error) {
       handleError(error);
     }
  }, [initialLimit, handleError, updateState]);

  const searchNews = useCallback(async (query: string, limit = initialLimit) => {
    if (!query.trim()) {
      await fetchNews(1, limit);
      return;
    }

    try {
      updateState({ loading: true, error: null });
      
      // Simulate API delay for better UX
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const searchResults = searchStaticNews(query.trim());
      const limitedResults = searchResults.slice(0, limit);
      
      updateState({
        news: limitedResults,
        loading: false,
        currentPage: 1,
        totalPages: 1,
        total: searchResults.length,
        hasMore: false
      });
    } catch (error) {
       handleError(error);
     }
  }, [initialLimit, handleError, updateState]);

  const loadMore = useCallback(async () => {
    if (!state.hasMore || state.loading) return;
    await fetchNews(state.currentPage + 1, initialLimit);
  }, [state.hasMore, state.loading, state.currentPage, fetchNews, initialLimit]);

  const refresh = useCallback(async () => {
    await fetchNews(1, initialLimit);
  }, [fetchNews, initialLimit]);

  const clearError = useCallback(() => {
    updateState({ error: null });
  }, [updateState]);

  const reset = useCallback(() => {
    setState({
      news: [],
      loading: false,
      error: null,
      hasMore: true,
      currentPage: 1,
      totalPages: 1,
      total: 0
    });
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchNews();
    }
  }, [autoFetch]); // Only depend on autoFetch to avoid infinite loops

  return {
    ...state,
    fetchNews,
    searchNews,
    loadMore,
    refresh,
    clearError,
    reset
  };
};

export default useNews;